import Service, { useService } from 'react-reservice';

import requestOtpAPI, { EOTPType } from '@webtrader/core/api/account/request-otp';
import signUpAPI, { ECustomerType } from '@webtrader/core/api/account/signUp';
import { ECrmType } from '@webtrader/core/enum/crm';
import i18n from '@webtrader/core/i18n';
import { IApiError } from '@webtrader/core/request/api';
import { mapResponseCodeToMessage } from '@webtrader/core/request/message';
import { IServiceApiBaseResponse } from '@webtrader/core/services';

export interface ISignUpSubmitParams {
  customerType: ECustomerType;
  countryId?: number;
  email?: string;
  password?: string;
  otp?: string;
  tncAcknowledged?: boolean;
}

interface ISignUpContext {
  submitting?: boolean;
  requestOtpLoading: boolean;
}

const defaultContext: ISignUpContext = {
  submitting: false,
  requestOtpLoading: false
};

const STONEINNO_CRM_TYPE = ECrmType.STONEINNO;

class SignUpContext extends Service<ISignUpContext> {
  constructor() {
    super();

    this.context = {
      ...defaultContext
    };
  }

  public reset = () => {
    this.context = {
      ...defaultContext
    };
  };

  public requestOtp = async (email: string): Promise<IServiceApiBaseResponse> => {
    this.context.requestOtpLoading = true;

    // Hardcode to stoneinno client portal url and crm type
    try {
      const { result } = await requestOtpAPI({
        email,
        type: EOTPType.EMAIL,
        crmType: STONEINNO_CRM_TYPE
      });
      if (result) {
        return {
          success: true,
          message: 'Success',
          params: result
        };
      }

      return {
        message: i18n.t('Unexpected error')
      };
    } catch (err) {
      const { code, message } = err as IApiError;

      return {
        message: mapResponseCodeToMessage(code, message)
      };
    } finally {
      this.context.requestOtpLoading = false;
    }
  };

  public submit = async (params: Required<ISignUpSubmitParams>): Promise<IServiceApiBaseResponse> => {
    try {
      this.context.submitting = true;

      const { customerType, countryId, email, password, otp } = params;

      // Hardcode to stoneinno client portal url and crm type
      const { result } = await signUpAPI({
        customerType,
        countryId,
        email,
        password,
        otp,
        crmType: STONEINNO_CRM_TYPE
      });

      if (result) {
        return {
          success: true,
          message: 'Success',
          params: result
        };
      }

      return {
        message: i18n.t('Unexpected error')
      };
    } catch (err) {
      const { code, message } = err as IApiError;

      return {
        message: mapResponseCodeToMessage(code, message)
      };
    } finally {
      this.context.submitting = false;
    }
  };
}

const signUpService = new SignUpContext();

export default signUpService;

export const useSignUp = () => {
  return useService(signUpService, (state) => state);
};
